import { AutoFixHigh } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  TextField,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parsingMethodsApi } from 'api';
import { ParsingMethodType, StatusCode } from 'enums';
import { useMeta, useMutation, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';

type Values = {
  template: string;
  type: ParsingMethodType;
};

type Props = {
  defaultSampleSms?: string;
  onApply?: (sampleSms: string) => void;
};

export const GenerateTemplateButton: React.FC<Props> = ({
  defaultSampleSms = '',
  onApply,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.parsing_methods',
  });
  const { isAdminOrTechOperator } = useUser();
  const { isGptTemplateGenerationEnabled } = useMeta();
  const { values, setFieldValue } = useFormikContext<Values>();

  const [open, setOpen] = useState(false);
  const [sampleSms, setSampleSms] = useState(defaultSampleSms);
  const [generatedTemplate, setGeneratedTemplate] = useState('');

  const trimmedSampleSms = useMemo(() => sampleSms.trim(), [sampleSms]);

  const getErrorMessage = useCallback(
    (error: AxiosError) =>
      error.response?.status === StatusCode.TooManyRequests
        ? t('generate.rate_limit_error')
        : t('generate.error'),
    [t],
  );

  const generateMutation = useMutation(
    () => parsingMethodsApi.generateTemplate(trimmedSampleSms, values.type),
    {
      notifierMessages: {
        loading: null,
        success: null,
        error: getErrorMessage,
      },
      onSuccess: ({ template }) => setGeneratedTemplate(template),
    },
  );

  const isVisible = useMemo(
    () => isAdminOrTechOperator && isGptTemplateGenerationEnabled,
    [isAdminOrTechOperator, isGptTemplateGenerationEnabled],
  );

  const handleToggle = useCallback(() => setOpen((prev) => !prev), []);

  const handleSampleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setSampleSms(e.target.value),
    [],
  );

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setGeneratedTemplate(e.target.value),
    [],
  );

  const handleGenerate = useCallback(
    () => generateMutation.mutate(),
    [generateMutation],
  );

  const handleApply = useCallback(() => {
    setFieldValue('template', generatedTemplate);
    onApply?.(trimmedSampleSms);
  }, [setFieldValue, generatedTemplate, onApply, trimmedSampleSms]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box>
      <Button
        type="button"
        size="small"
        variant="text"
        color="primary"
        startIcon={<AutoFixHigh />}
        onClick={handleToggle}
      >
        {t('generate.button')}
      </Button>
      <Collapse in={open}>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label={t('generate.fields.sample_sms')}
            value={sampleSms}
            onChange={handleSampleChange}
            fullWidth
            multiline
            rows={4}
            required
          />
          <Collapse in={!!generatedTemplate}>
            <TextField
              label={t('generate.fields.preview')}
              value={generatedTemplate}
              onChange={handleTemplateChange}
              fullWidth
              multiline
              rows={3}
            />
          </Collapse>
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={!trimmedSampleSms || generateMutation.isLoading}
              startIcon={
                generateMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoFixHigh />
                )
              }
            >
              {t('generate.actions.generate')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={handleApply}
              disabled={!generatedTemplate}
            >
              {t('generate.actions.apply')}
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};
